import type { ParameterValueCreateOperation } from '@festina/configuration-general-types/configuration'
import { useCallback } from 'react'

import { partitionValueOperations } from '../parameterOperations/partitionValueOperations'
import type { OperationWithSourceInfo } from '../types'
import { processValueOperationsForViewConstraints } from './helpers'
import type { ResolveValueOperationsProps } from './useLazilyResolveValueOperations'
import { useLazilyResolveValueOperations } from './useLazilyResolveValueOperations'

type Props = ResolveValueOperationsProps & {
  isFlattened: boolean
}

/**
 * Resolves the given value operations and keeps the create operations only, ready for the
 * CSV exports this feeds: the server side squash and CSV conversion cannot represent amend
 * semantics. Partitioning here rather than inside the resolution hook keeps that hook to
 * resolving, and lets a consumer that wants the amendments read them from it directly.
 *
 * The create-only return type states the intent, but the compiler cannot hold anyone to
 * it: the generated operations share their entity and model their discriminator as the
 * literal union'ed with `(string & {})`, so create and amend are mutually assignable. The
 * guarantee comes from the partition seam's runtime type guard.
 */
export const useLazilyResolveCreateValueOperations = () => {
  const { mutateAsync: resolveValueOperations, isPending } =
    useLazilyResolveValueOperations()

  const resolveCreates = useCallback(
    async ({
      allValues,
      server,
      isFlattened,
    }: Props): Promise<
      OperationWithSourceInfo<ParameterValueCreateOperation>[]
    > => {
      const resolvedOperations = await resolveValueOperations({
        allValues,
        server,
      })
      const { creates } = partitionValueOperations(resolvedOperations)

      return processValueOperationsForViewConstraints({
        allOperations: creates,
        isFlattened,
      })
    },
    [resolveValueOperations],
  )

  return { mutateAsync: resolveCreates, isPending }
}
